import api from '@/api'
import dayjs from 'dayjs'
import baseJs from '@/transformer/base'
import {
  toDisplayAnnouncement,
  toArrangeAnnouncement,
  getArrangedAnnouncement,
  getEmptyAnnouncementInfo
} from '@/transformer/announcement'

const getDefaultState = () => {
  return {
    announcements: [],
    announcement: getEmptyAnnouncementInfo(),
    listReaction: [],
    studentProfileUrls: [],
    infoApiStatus: baseJs.getApiStatusEmpty(),
    listApiStatus: baseJs.getApiStatusEmpty(),
    listReactionApiStatus: baseJs.getApiStatusEmpty()
  }
}

const mutations = {
  setAnnouncements(state, announcements) {
    state.announcements = [...announcements]
  },
  setAnnouncement(state, announcement) {
    state.announcement = { ...announcement }
  },
  setListReaction(state, listReaction) {
    state.listReaction = [...listReaction]
  },
  setStudentProfileUrls(state, studentProfileUrls) {
    state.studentProfileUrls = [...studentProfileUrls]
  },
  setListApiStatus(state, apiStatus) {
    state.listApiStatus = { ...apiStatus }
  },
  setInfoApiStatus(state, apiStatus) {
    state.infoApiStatus = { ...apiStatus }
  },
  setListReactionApiStatus(state, apiStatus) {
    state.listReactionApiStatus = { ...apiStatus }
  }
}

const actions = {
  async getAnnouncementList({ commit }) {
    await commit('setListApiStatus', baseJs.getApiStatusEmpty())
    try {
      const res = await api.announcement.list()
      await commit('setAnnouncements', res.list)
      await commit('setListApiStatus', baseJs.getApiStatusSuccess())
      return Promise.resolve()
    } catch (err) {
      const errorStatus = baseJs.getErrorStatus(err)
      await commit('setListApiStatus', { ...errorStatus })
      return Promise.reject(err)
    }
  },
  async getInfo({ dispatch }, { announcementId }) {
    await Promise.all([
      dispatch('getAnnouncementInfo', announcementId),
      dispatch('getListReaction', announcementId) // 返信一覧も更新
    ])
  },
  async getAnnouncementInfo({ commit }, announcementId) {
    commit('setInfoApiStatus', baseJs.getApiStatusEmpty())
    try {
      const res = await api.announcement.info(Number(announcementId))
      await Promise.all([
        commit('setAnnouncement', res.list[0]),
        commit('setInfoApiStatus', baseJs.getApiStatusSuccess())
      ])
      return Promise.resolve()
    } catch (err) {
      const errorStatus = baseJs.getErrorStatus(err)
      await commit('setInfoApiStatus', { ...errorStatus })
      return Promise.reject(err)
    }
  },
  async getListReaction({ commit }, announcementId) {
    commit('setListReactionApiStatus', baseJs.getApiStatusEmpty())
    try {
      const res = await api.announcement.listReaction(Number(announcementId))
      const reactionList = res.list.map((data) => {
        return {
          id: data.to_user_id,
          name: data.user_name,
          status: data.status,
          url_string: data.url_string,
          reply: data.reply,
          comment: data.comment,
          updated_at: data.updated_at,
          icon: data.icon,
          is_deleted: data.is_deleted
        }
      })
      await Promise.all([
        commit('setListReaction', reactionList),
        commit('setListReactionApiStatus', baseJs.getApiStatusSuccess())
      ])
      return Promise.resolve()
    } catch (err) {
      const errorStatus = baseJs.getErrorStatus(err)
      await commit('setListReactionApiStatus', { ...errorStatus })
      return Promise.reject(err)
    }
  },

  async reply({ commit }, { announcementId, replyType, comment }) {
    try {
      const res = await api.announcement.reply(announcementId, replyType, comment)
      return Promise.resolve(res)
    } catch (err) {
      return Promise.reject(err)
    }
  },
  async favorite({ commit }, { announcementId, toBeFavorite }) {
    try {
      const res = await api.announcement.favorite(announcementId, toBeFavorite)
      return Promise.resolve(res)
    } catch (err) {
      return Promise.reject(err)
    }
  },
  updateStudentUrls({ commit }, reactionList) {
    const studentUrls = reactionList.filter((data) => data.url_string && data.is_deleted !== '1')
    commit('setStudentProfileUrls', studentUrls)
  }
}

const getters = {
  /**
   * 最近のお知らせ(最大5件)
   */
  announcementsForRecent(state) {
    const today = dayjs(new Date())
    const filteredAnnouncements = state.announcements
      .filter((announcement) => {
        // ドラフトではない && 公開日以降 && 終了日以内
        // 期限なしの場合はそれ以外
        if (!announcement.ended_at) {
          return Number(announcement.is_draft) !== 1 && today.isAfter(dayjs(announcement.started_at))
        } else {
          return (
            Number(announcement.is_draft) !== 1 &&
            today.isAfter(dayjs(announcement.started_at)) &&
            today.isBefore(dayjs(announcement.ended_at))
          )
        }
      })
      .map((announcement) => toDisplayAnnouncement(announcement))

    filteredAnnouncements.sort((a, b) => {
      if (a.updated_at > b.updated_at) {
        return -1
      }
      return 1
    })

    if (filteredAnnouncements.length > 5) {
      return filteredAnnouncements.slice(0, 5)
    } else {
      return filteredAnnouncements
    }
  },
  selectedAnnouncement(state) {
    return toDisplayAnnouncement(toArrangeAnnouncement(state.announcement))
  },
  announcementList(state) {
    return state.announcements.map((val) => {
      return getArrangedAnnouncement(val)
    })
  },
  listReactionDesc(state) {
    const sortedReactions = state.listReaction.slice()
    return sortedReactions.sort((a, b) => {
      if (a.updated_at > b.updated_at) {
        return -1
      }
      return 1
    })
  }
}

export default {
  namespaced: true,
  state: getDefaultState(),
  mutations,
  actions,
  getters
}
